/**
 * The short lists of projects and offers in the sidebar: which ones, in which
 * order, and where a click leads. Pure functions plus the browser's memory of
 * what was opened last - per browser, nothing is sent to the server.
 */

export type QuickKind = 'projects' | 'offers';

/** At most this many entries per short list. */
export const QUICK_MAX = 6;
const KEEP = 20;
const RECENT_KEY = 'pst.nav.recent.';
const OPEN_KEY = 'pst.nav.open.';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const QUICK_BASE: Record<QuickKind, string> = { projects: '/projekte', offers: '/angebote' };

/** The project or offer a path is inside, or null on any other page. */
export function idInPath(kind: QuickKind, pathname: string): string | null {
  const [, first, id] = pathname.split('/');
  return `/${first}` === QUICK_BASE[kind] && id && UUID.test(id) ? id : null;
}

/**
 * Where a click on another project or offer leads: into the same area one is
 * in (from project A's "Zeiten" to project B's), else to its start page.
 */
export function switchPath(kind: QuickKind, pathname: string, id: string): string {
  const base = `${QUICK_BASE[kind]}/${id}`;
  if (!idInPath(kind, pathname)) return base;
  const area = pathname.split('/')[3];
  return area ? `${base}/${area}` : base;
}

/** The one opened last first; each id once; at most `KEEP`. */
export function withRecent(list: readonly string[], id: string): string[] {
  return [id, ...list.filter((x) => x !== id)].slice(0, KEEP);
}

/**
 * The entries of a short list: the ones opened last, then the rest of the
 * candidates in their order, at most `max`. The current one always shows.
 */
export function pickQuick<T extends { id: string }>(
  all: readonly T[],
  candidates: (item: T) => boolean,
  recent: readonly string[],
  currentId: string | null,
  max = QUICK_MAX,
): T[] {
  const byId = new Map(all.map((x) => [x.id, x]));
  const out: T[] = [];
  const seen = new Set<string>();
  const add = (x: T | undefined): void => {
    if (x && !seen.has(x.id) && out.length < max) { seen.add(x.id); out.push(x); }
  };
  if (currentId) add(byId.get(currentId));
  for (const id of recent) add(byId.get(id));
  for (const x of all) if (candidates(x)) add(x);
  return out;
}

export function readRecent(kind: QuickKind): string[] {
  try {
    const v = JSON.parse(window.localStorage.getItem(RECENT_KEY + kind) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch { return []; }
}

export function noteRecent(kind: QuickKind, id: string): void {
  try { window.localStorage.setItem(RECENT_KEY + kind, JSON.stringify(withRecent(readRecent(kind), id))); } catch { /* private window */ }
}

export function readOpen(kind: QuickKind): boolean {
  try { return window.localStorage.getItem(OPEN_KEY + kind) !== '0'; } catch { return true; }
}

export function keepOpen(kind: QuickKind, open: boolean): void {
  try { window.localStorage.setItem(OPEN_KEY + kind, open ? '1' : '0'); } catch { /* private window */ }
}
